import WordpressContent from 'components/Content/WordpressContent';
import { getPage } from 'graphql/cmsQueries';
import { GetServerSidePropsContext } from 'next';
import { parseContextLocale } from 'utils/parseContextLocale';
import { parseRouteParams } from 'utils/parseRouteParams';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { ConsultantCta } from 'components/Product/components/ProductDetails/components/ConsultantCta';
import { ConsultantCategory } from 'components/Consultant/Categories/ConsultantCategory';
import useMarket from 'stores/useMarket';
import BigBanner from '../../../public/big-banner.png';
import { ProfileLayout } from '../../components/Consultant/ProfileLayout';
import { ProfilePageContent, ProfileProps } from '../../components/Consultant/ProfilePageContent';
import PageMeta from '../../components/PageMeta';
import { OrganisationPublicContent, Product, useGetConsultantRecommendationsQuery } from '../../generated/graphql';
import { getConsultantPublicContentBySlug } from '../../graphql/consultantQueries';
import { getCommonServerSideProps } from '../../server/getCommonServerSideProps';

type PageProps = ProfileProps & {
    content: string;
    slug: string;
};

function getFullName(publicContent: OrganisationPublicContent) {
    return `${publicContent.first_name} ${publicContent.last_name}`;
}

export default function Page(props: PageProps) {
    const { t } = useServerTranslation();
    const { market } = useMarket();

    const { publicContent, content, slug } = props;
    const consultantContent = addConsultantSlugToLinks(content, slug);

    const { data, loading, error } = useGetConsultantRecommendationsQuery({
        variables: { id: publicContent.id },
        fetchPolicy: 'network-only',
    });

    const consultantProducts = data?.organisationRecommendations;

    if (!data || loading || error) {
        return null;
    }

    const dailyOffers: Product[] | null | undefined = consultantProducts?.dailyOffers;
    const specialOffers: Product[] | null | undefined = consultantProducts?.specialOffers;
    const favourites: Product[] | null | undefined = consultantProducts?.favourites;

    return (
        <ProfileLayout backgroundImageSrc={BigBanner.src}>
            <PageMeta
                title={t('seo.title.consultant-profile-page', { fullName: getFullName(publicContent) })}
            />
            <ProfilePageContent {...props}>
                <ConsultantCta />
                {dailyOffers != null && dailyOffers.length > 0 && <ConsultantCategory organisationId={publicContent.id} title={t('profile.my-daily-offers')} products={dailyOffers} />}
                {specialOffers != null && specialOffers.length > 0 && <ConsultantCategory organisationId={publicContent.id} title={t('profile.my-special-offers')} products={specialOffers} />}
                {favourites != null && favourites.length > 0 && <ConsultantCategory organisationId={publicContent.id} title={t('profile.my-favourites')} products={favourites} />}
                <WordpressContent content={consultantContent} />
            </ProfilePageContent>
        </ProfileLayout>
    );
}

function addConsultantSlugToLinks(html: string, slug: string) {
    const pattern = /href="(https?:\/\/[\w.-]+)?(\/[produkter|tuotteet|catalogsearch][^"'\s>]*)"/gi;
    const consultantSlug = `/consultant/${slug}`;

    return html.replace(pattern, (match, domain, path) => `href="${consultantSlug}${path}"`);
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const [consultant] = parseRouteParams(context, ['consultant']);
    const { market } = await parseContextLocale(context);
    const result = await getConsultantPublicContentBySlug(market, consultant);

    if (!result?.data?.organisationPublicContent) {
        return { notFound: true };
    }

    const page = await getPage(market, 'magento-frontpage');

    return getCommonServerSideProps({
        context,
        additionalProps: {
            publicContent: result?.data?.organisationPublicContent || '',
            slug: consultant,
            content: page?.content ?? null,
        },
    });
}
